import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Eye, EyeOff, Trash2, KeyRound } from 'lucide-react';

const Agent = () => {
  const [agents, setAgents] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [render, setRender] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, agentId: null });
  const [resetModal, setResetModal] = useState({ open: false, agentId: null });
  const [newPassword, setNewPassword] = useState('');
  const orgName = sessionStorage.getItem('organizationName');

  useEffect(() => {
    fetchAgents();
    setRender(false);
  }, [render]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/getAgent`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(`${orgName}_token`)}`,
        },
      });
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const addAgent = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/addAgent`, {
        email,
        password,
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(`${orgName}_token`)}`,
        },
      });
      setAgents([...agents, response.data]);
      setEmail('');
      setPassword('');
      setShowPassword(false);
      setRender(true);
      toast.success('Agent added successfully');
    } catch (error) {
      console.error('Error adding agent:', error);
      toast.error('Error adding agent');
    }
  };

  const deleteAgent = async (agentId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/deleteAgent/${agentId}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(`${orgName}_token`)}`,
        },
      });
      toast.success('Agent deleted successfully');
      setRender(true);
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Error deleting agent');
    }
  };

  const updateAgentPassword = async (agentId) => {
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/updateAgentPassword/${agentId}`, {
        password: newPassword,
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem(`${orgName}_token`)}`,
        },
      });
      toast.success('Password updated successfully');
      setResetModal({ open: false, agentId: null });
      setNewPassword('');
      setRender(true);
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Error updating password');
    }
  };

  const togglePasswordVisibility = (agentId) => {
    setVisiblePasswords((prev) => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  return (
    <div className="min-h-screen bg-gray-100 p-12">
      <ToastContainer autoClose={3000}/>
      <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-lg flex flex-col">
        <h2 className="text-lg font-bold mb-4">Create Agent</h2>
        <div className="mb-4">
          <label className="block text-gray-700">Email</label>
          <input
            type="email"
            className="w-full p-2 border border-gray-300 rounded mt-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className="w-full p-2 border border-gray-300 rounded mt-1 pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 mt-0.5"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div className='flex justify-end'>
        <button
          className="bg-blue-700 text-white  items-center px-3 py-1 rounded hover:bg-blue-600"
          onClick={addAgent}
        >
          Add Agent
        </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto mt-6 bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-lg font-bold mb-4">Agents List</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b text-left">ID</th>
                <th className="py-2 px-4 border-b text-left">Email</th>
                <th className="py-2 px-4 border-b text-left">Password</th>
                <th className="py-2 px-4 border-b text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent, index) => (
                <tr key={index}>
                  <td className="py-2 px-4 border-b">{agent.id}</td>
                  <td className="py-2 px-4 border-b">{agent.email}</td>
                  <td className="py-2 px-4 border-b">
                    <div className="flex items-center gap-2">
                      <span>{visiblePasswords[agent.id] ? agent.password : '••••••••'}</span>
                      <button
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => togglePasswordVisibility(agent.id)}
                      >
                        {visiblePasswords[agent.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b">
                    <div className="flex items-center gap-2">
                      <button
                        title="Reset Password"
                        className="p-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => { setResetModal({ open: true, agentId: agent.id }); setNewPassword(''); }}
                      >
                        <KeyRound size={15} />
                      </button>
                      <button
                        title="Delete"
                        className="p-1.5 rounded bg-red-700 hover:bg-red-800 text-white"
                        onClick={() => setConfirmDialog({ open: true, agentId: agent.id })}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Agent</h3>
            <p className="text-sm text-gray-500 mb-4">Are you sure you want to permanently delete this agent? This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                onClick={() => setConfirmDialog({ open: false, agentId: null })}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                onClick={() => {
                  deleteAgent(confirmDialog.agentId);
                  setConfirmDialog({ open: false, agentId: null });
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Reset Password</h2>
            <input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border px-3 py-2 rounded mb-4"
            />
            <div className="flex justify-end">
              <button
                onClick={() => { setResetModal({ open: false, agentId: null }); setNewPassword(''); }}
                className="mr-3 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => updateAgentPassword(resetModal.agentId)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agent;
